import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '@/i18n/config';
import { addLang, useLang } from '@/i18n/useLocalizedPath';

/**
 * Language switcher dropdown. Navigates to the language-prefixed version of the
 * current URL so each language has a distinct, crawlable URL (good for hreflang/SEO).
 *
 * variant:
 *  - "desktop": compact pill button (header)
 *  - "mobile": full-width list of options (mobile menu)
 */
export default function LanguageSwitcher({ variant = 'desktop' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const lang = useLang();
  const active = SUPPORTED_LANGUAGES.find((l) => l.code === lang) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const change = (code) => {
    setOpen(false);
    if (code === lang) return;
    navigate(`${addLang(pathname, code)}${search || ''}`);
  };

  if (variant === 'mobile') {
    return (
      <div className="px-2 py-2" data-testid="language-switcher-mobile">
        <div className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-2">
          <Globe className="w-4 h-4" /> {active.label}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => change(l.code)}
              data-testid={`language-option-mobile-${l.code}`}
              className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                l.code === lang
                  ? 'bg-orange-50 text-orange-600 border border-orange-200'
                  : 'text-gray-700 hover:bg-gray-50 border border-transparent'
              }`}
            >
              <span>{l.label}</span>
              {l.code === lang && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={active.label}
        aria-expanded={open}
        data-testid="language-switcher-button"
        className="flex items-center space-x-1.5 px-3 py-2 rounded-full border border-gray-200 text-gray-700 hover:text-orange-600 hover:border-orange-300 transition-colors text-sm font-medium"
      >
        <Globe className="w-4 h-4" />
        <span>{active.short}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50"
          data-testid="language-switcher-menu"
        >
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => change(l.code)}
              data-testid={`language-option-${l.code}`}
              className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                l.code === lang
                  ? 'text-orange-600 font-semibold bg-orange-50'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-400 w-6">{l.short}</span>
                {l.label}
              </span>
              {l.code === lang && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
